// ==================================================================================
// Digital Approval System (DAS) - Approval Actions Service
// ==================================================================================
// Service functions for approval action operations
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse } from '../../types/common.types';
import { Request } from '../../types/requests.types';
import {
  ApproveRequestRequest,
  RejectRequestRequest,
  EscalateRequestRequest,
  RecommendRequestRequest,
  ReturnForClarificationRequest,
  ReturnForClarificationResponse,
  ResubmitRequestRequest,
  ResubmitRequestResponse,
  CancelRequestRequest,
  CancelRequestResponse,
  AddCommentRequest,
  EligibleReturnRecipient,
} from '../../types/das/approvalActions.types';
import { approvalActionEndpoints } from '../../endpoints/das/approvalActions.endpoints';

/**
 * Approval actions service
 */
export const approvalActionsService = {
  /**
   * Approve a request
   */
  approve: async (id: string, request: ApproveRequestRequest): Promise<Request> => {
    const response = await axiosClient.post<ApiResponse<Request>>(
      approvalActionEndpoints.approve(id),
      request
    );
    return response.data.data;
  },

  /**
   * Reject a request
   */
  reject: async (id: string, request: RejectRequestRequest): Promise<Request> => {
    const response = await axiosClient.post<ApiResponse<Request>>(
      approvalActionEndpoints.reject(id),
      request
    );
    return response.data.data;
  },

  /**
   * Escalate a request
   */
  escalate: async (id: string, request: EscalateRequestRequest): Promise<Request> => {
    const response = await axiosClient.post<ApiResponse<Request>>(
      approvalActionEndpoints.escalate(id),
      request
    );
    return response.data.data;
  },

  /**
   * Recommend approval/rejection
   */
  recommend: async (id: string, request: RecommendRequestRequest): Promise<Request> => {
    const response = await axiosClient.post<ApiResponse<Request>>(
      approvalActionEndpoints.recommend(id),
      request
    );
    return response.data.data;
  },

  /**
   * Get users eligible to receive a returned request
   * Returns the original requestor and any approvers who have already taken action
   */
  getEligibleReturnRecipients: async (id: string): Promise<EligibleReturnRecipient[]> => {
    const response = await axiosClient.get<ApiResponse<EligibleReturnRecipient[]>>(
      approvalActionEndpoints.getEligibleReturnRecipients(id)
    );
    return response.data.data;
  },

  /**
   * Return for clarification
   * Optionally specify returnToUserId to return to a previous approver instead of the original requestor
   */
  returnForClarification: async (
    id: string,
    request: ReturnForClarificationRequest
  ): Promise<ReturnForClarificationResponse> => {
    const response = await axiosClient.post<ApiResponse<ReturnForClarificationResponse>>(
      approvalActionEndpoints.returnForClarification(id),
      request
    );
    return response.data.data;
  },

  /**
   * Resubmit a returned request
   */
  resubmit: async (
    id: string,
    request: ResubmitRequestRequest
  ): Promise<ResubmitRequestResponse> => {
    const response = await axiosClient.post<ApiResponse<ResubmitRequestResponse>>(
      approvalActionEndpoints.resubmit(id),
      request
    );
    return response.data.data;
  },

  /**
   * Cancel a request (only by creator)
   */
  cancel: async (
    id: string,
    request: CancelRequestRequest
  ): Promise<CancelRequestResponse> => {
    const response = await axiosClient.post<ApiResponse<CancelRequestResponse>>(
      approvalActionEndpoints.cancel(id),
      request
    );
    return response.data.data;
  },

  /**
   * Add a comment
   */
  addComment: async (id: string, request: AddCommentRequest): Promise<boolean> => {
    const response = await axiosClient.post<ApiResponse<boolean>>(
      approvalActionEndpoints.addComment(id),
      request
    );
    return response.data.data;
  },

  // ==================================================================================
  // RETURNED REQUESTS
  // ==================================================================================

  /**
   * Get returned requests for current user (as requestor)
   */
  getMyReturned: async (topN?: number): Promise<Request[]> => {
    const response = await axiosClient.get<ApiResponse<Request[]>>(
      approvalActionEndpoints.getMyReturned(topN)
    );
    return response.data.data;
  },

  /**
   * Get requests returned by current user (as approver)
   */
  getReturnedByMe: async (topN?: number): Promise<Request[]> => {
    const response = await axiosClient.get<ApiResponse<Request[]>>(
      approvalActionEndpoints.getReturnedByMe(topN)
    );
    return response.data.data;
  },
};
